import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { printLabelPickSheetPdf, printLaserSheet } from '../utils/qzTrayService';
import Modal from './Modal';
import './LabelPrintModal.css';

const ACTION_OPTIONS = [
  { value: 'add_stock', label: 'New Stock' },
  { value: 'restock_line', label: 'Restock' },
  { value: 'return', label: 'Return' },
  { value: 'mark_used', label: 'Mark Used' },
  { value: 'batch_receive', label: 'Batch Receive' },
  { value: 'stock_audit', label: 'Stock Audit' },
];

const MODE_OPTIONS = [
  { value: 'pending_only', label: 'Pending only' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'specific_date', label: 'Specific date' },
  { value: 'date_range', label: 'Date range' },
];

const DEFAULT_LAYOUT = {
  columns: 3,
  rows: 11,
  labelsPerSheet: 33,
  pageWidthMm: 210,
  pageHeightMm: 297,
  marginLeftMm: 6,
  marginTopMm: 8.8,
  labelWidthMm: 66,
  labelHeightMm: 25.4,
  pitchXmm: 66,
  pitchYmm: 25.4,
};

const DEFAULT_STICKER_STYLE = {
  textCase: 'as_typed',
};

const DEFAULT_PRINT_JOB_FORMATS = {
  batch_receive: {
    label: 'Batch Receive',
    template: 'ORDER_SLIP_2X1',
    line1: '{orderDisplayId}',
    line2: '{sku}',
    line3: '{qty}m',
    barcodeText: '{orderDisplayId}',
    htmlTemplate: '',
  },
  stock_audit: {
    label: 'Stock Audit',
    template: 'PIECE_2X1',
    line1: 'PIECE: {pieceId}  LOC: {location}',
    line2: '{sku}',
    line3: '{qty}m  [{auditDate}]',
    barcodeText: '{pieceId}',
    htmlTemplate: '',
  },
};

function todayKey(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function normalizeLayout(input) {
  const merged = { ...DEFAULT_LAYOUT, ...((input && typeof input === 'object') ? input : {}) };
  const columns = Math.max(1, Number(merged.columns || DEFAULT_LAYOUT.columns));
  const rows = Math.max(1, Number(merged.rows || DEFAULT_LAYOUT.rows));
  return { ...merged, columns, rows, labelsPerSheet: columns * rows };
}

function normalizeSlot(value, layout) {
  const max = Math.max(1, Number(layout.labelsPerSheet || 33));
  const n = Number(value || 1);
  return Number.isFinite(n) ? Math.min(max, Math.max(1, Math.round(n))) : 1;
}

function actionLabel(action) {
  return ACTION_OPTIONS.find((o) => o.value === action)?.label || action || 'Label';
}

function formatJobTime(job) {
  if (job?.createdAtIso) {
    const d = new Date(job.createdAtIso);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  }
  const seconds = job?.createdAt?._seconds;
  if (seconds != null) return new Date(Number(seconds) * 1000).toLocaleString();
  return '-';
}

function isLikelyProductSku(value) {
  const v = String(value || '').trim();
  if (!v) return false;
  if (/^\d+$/.test(v)) return false;
  return /[A-Za-z]/.test(v);
}

function getPickSheetSku(job) {
  const action = String(job?.meta?.action || '').trim();
  const payload = job?.payload || {};
  const candidates = action === 'mark_used'
    ? [job?.meta?.sku, payload.line2, payload.barcodeText, payload.line1]
    : [job?.meta?.sku, payload.line1, payload.barcodeText, payload.line2];
  const likely = candidates.find(isLikelyProductSku);
  if (likely) return String(likely).trim().toUpperCase();
  const fallback = candidates.find((value) => String(value || '').trim());
  return String(fallback || '').trim().toUpperCase();
}

function isPrintablePendingJob(job) {
  const id = String(job?.id || '');
  return !!id && !id.startsWith('historical_ledger_') && String(job?.status || '').toLowerCase() === 'pending';
}

function renderFormatValue(value, data) {
  return String(value || '').replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, key) => {
    const next = data?.[key];
    return next == null ? '' : String(next);
  });
}

function applyTextCase(value, style) {
  const text = String(value || '');
  return style?.textCase === 'uppercase' ? text.toUpperCase() : text;
}

function normalizePrintJobFormats(input, defaults = DEFAULT_PRINT_JOB_FORMATS) {
  const source = (input && typeof input === 'object') ? input : {};
  return {
    ...defaults,
    ...Object.fromEntries(Object.entries(source).map(([key, value]) => [
      key,
      { ...(defaults[key] || {}), ...((value && typeof value === 'object') ? value : {}) },
    ])),
  };
}

function cleanSeparators(str) {
  return String(str || '')
    .replace(/\s*•\s*•+/g, ' • ')
    .replace(/^(\s*•\s*)+/g, '')
    .replace(/(\s*•\s*)+$/g, '')
    .replace(/\s+•\s+/g, ' • ')
    .trim();
}

function renderFormatPayload(format, data, style = DEFAULT_STICKER_STYLE) {
  const payload = {
    line1: cleanSeparators(applyTextCase(renderFormatValue(format?.line1, data), style)).slice(0, 48),
    line2: cleanSeparators(applyTextCase(renderFormatValue(format?.line2, data), style)).slice(0, 48),
    line3: cleanSeparators(applyTextCase(renderFormatValue(format?.line3, data), style)).slice(0, 32),
    barcodeText: applyTextCase(renderFormatValue(format?.barcodeText, data), style).slice(0, 64).trim(),
  };
  const templateData = {
    ...(data || {}),
    line1: payload.line1,
    line2: payload.line2,
    line3: payload.line3,
    barcode: payload.barcodeText,
    barcodeText: payload.barcodeText,
  };
  return {
    ...payload,
    htmlTemplate: format?.htmlTemplate ? renderFormatValue(format.htmlTemplate, templateData) : '',
  };
}

export default function LabelPrintModal({
  isOpen,
  onClose,
  apiBase = '',
  modeContext = 'inventory',
  batchId = '',
  batchLabelRows = [],
  defaultAction = '',
}) {
  const { currentUser } = useAuth();
  const isBatchMode = modeContext === 'batch';
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [nextSlot, setNextSlot] = useState(1);
  const [printJobFormats, setPrintJobFormats] = useState(DEFAULT_PRINT_JOB_FORMATS);
  const [printStickerStyle, setPrintStickerStyle] = useState(DEFAULT_STICKER_STYLE);
  const [actions, setActions] = useState(() => (defaultAction ? [defaultAction] : []));
  const [mode, setMode] = useState('pending_only');
  const [includePrinted, setIncludePrinted] = useState(false);
  const [date, setDate] = useState(todayKey());
  const [dateFrom, setDateFrom] = useState(todayKey(-1));
  const [dateTo, setDateTo] = useState(todayKey());
  const [search, setSearch] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lastBatch, setLastBatch] = useState(null);
  const [markPrinted, setMarkPrinted] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pickSheetImageCache, setPickSheetImageCache] = useState({});
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [rowSavingId, setRowSavingId] = useState('');

  const loadSettings = useCallback(async () => {
    if (!currentUser || !isOpen) return;
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(`${apiBase}/api/v1/print-labels/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.success !== true) throw new Error(data.message || 'Failed to load print settings.');
      const nextLayout = normalizeLayout(data.laserSheetLayout);
      setLayout(nextLayout);
      setNextSlot(normalizeSlot(data.laserSheetState?.nextSlot || 1, nextLayout));
      const formatDefaults = normalizePrintJobFormats(data.printJobFormatDefaults || DEFAULT_PRINT_JOB_FORMATS);
      setPrintJobFormats(normalizePrintJobFormats(data.printJobFormats || {}, formatDefaults));
      setPrintStickerStyle({
        ...DEFAULT_STICKER_STYLE,
        ...((data.printStickerStyle && typeof data.printStickerStyle === 'object') ? data.printStickerStyle : {}),
      });
    } catch (e) {
      setError(e?.message || 'Failed to load print settings.');
    }
  }, [apiBase, currentUser, isOpen]);

  const loadJobs = useCallback(async () => {
    if (!currentUser || !isOpen || isBatchMode) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const params = new URLSearchParams({
        mode,
        actions: actions.join(','),
        search,
        includePrinted: includePrinted ? '1' : '0',
        limit: '500',
      });
      if (mode === 'specific_date') params.set('date', date);
      if (mode === 'date_range') {
        params.set('dateFrom', dateFrom);
        params.set('dateTo', dateTo);
      }
      const resp = await fetch(`${apiBase}/api/v1/print-labels/jobs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.success !== true) throw new Error(data.message || 'Failed to load label jobs.');
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch (e) {
      setError(e?.message || 'Failed to load label jobs.');
    } finally {
      setLoading(false);
    }
  }, [actions, apiBase, currentUser, date, dateFrom, dateTo, includePrinted, isBatchMode, isOpen, mode, search]);

  useEffect(() => {
    if (!isOpen) return;
    loadSettings();
  }, [isOpen, loadSettings]);

  useEffect(() => {
    if (!isOpen) return;
    if (isBatchMode) return;
    loadJobs();
  }, [isBatchMode, isOpen, loadJobs]);

  useEffect(() => {
    if (!isOpen) return;
    setMarkPrinted(true);
    if (defaultAction) {
      setActions([defaultAction]);
    } else {
      setActions([]);
    }
  }, [isOpen, defaultAction]);

  const batchJobs = useMemo(() => {
    if (!isBatchMode) return [];
    const format = printJobFormats.batch_receive || DEFAULT_PRINT_JOB_FORMATS.batch_receive;
    const rows = Array.isArray(batchLabelRows) ? batchLabelRows : [];
    return rows.map((row, index) => {
      const qtyNum = Number(row?.qty || row?.quantity || 0);
      const qty = Number.isFinite(qtyNum) ? Number(qtyNum.toFixed(3)) : String(row?.qty || row?.quantity || '').trim();
      const data = {
        batchId: String(batchId || row?.batchId || '').trim(),
        batchNumber: String(row?.batchNumber || batchId || row?.batchId || '').trim(),
        orderDisplayId: String(row?.orderDisplayId || row?.orderNumber || '').trim(),
        orderNumber: String(row?.orderNumber || row?.orderDisplayId || '').trim(),
        sku: String(row?.sku || '').trim().toUpperCase(),
        qty,
        lineItemId: String(row?.lineItemId || '').trim(),
        pieceId: String(row?.pieceId || row?.batchId || batchId || '').trim(),
      };
      const payload = renderFormatPayload(format, data, printStickerStyle);
      return {
        id: `batch_${data.batchId || 'batch'}_${data.lineItemId || index}_${data.sku}_${index}`,
        status: 'batch_preprint',
        printerTarget: 'STICKER',
        format: 'ZPL',
        template: format.template || 'ORDER_SLIP_2X1',
        createdAtIso: null,
        payload,
        meta: {
          ...data,
          action: 'batch_receive',
          source: 'batch_preprint',
          productTitle: String(row?.productTitle || row?.fabricName || '').trim(),
          productImageUrl: String(row?.productImageUrl || row?.imageUrl || '').trim(),
        },
      };
    }).filter((job) => job.meta.sku && Number(job.meta.qty || 0) > 0);
  }, [batchId, batchLabelRows, isBatchMode, printJobFormats, printStickerStyle]);

  const sourceJobs = isBatchMode ? batchJobs : jobs;
  const capacity = Math.max(0, Number(layout.labelsPerSheet || 33) - Number(nextSlot || 1) + 1);
  const selectedJobIdSet = useMemo(() => new Set(selectedJobIds), [selectedJobIds]);
  const jobsToPrint = useMemo(
    () => sourceJobs.filter((job) => selectedJobIdSet.has(String(job?.id || ''))),
    [selectedJobIdSet, sourceJobs]
  );
  const firstPagePrintCount = Math.min(jobsToPrint.length, capacity);
  const pageCount = useMemo(() => {
    if (jobsToPrint.length === 0) return 0;
    const labelsPerSheet = Math.max(1, Number(layout.labelsPerSheet || 33));
    const remainingAfterFirst = Math.max(0, jobsToPrint.length - capacity);
    return 1 + Math.ceil(remainingAfterFirst / labelsPerSheet);
  }, [capacity, jobsToPrint.length, layout.labelsPerSheet]);
  const nextSlotAfterPrint = useMemo(() => {
    if (jobsToPrint.length === 0) return nextSlot;
    const labelsPerSheet = Math.max(1, Number(layout.labelsPerSheet || 33));
    const firstCapacity = Math.max(0, labelsPerSheet - Number(nextSlot || 1) + 1);
    if (jobsToPrint.length < firstCapacity) return normalizeSlot(Number(nextSlot || 1) + jobsToPrint.length, layout);
    if (jobsToPrint.length === firstCapacity) return 1;
    const remaining = jobsToPrint.length - firstCapacity;
    const finalPageUsed = remaining % labelsPerSheet;
    return finalPageUsed === 0 ? 1 : finalPageUsed + 1;
  }, [jobsToPrint.length, layout, nextSlot]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedJobIds(sourceJobs.map((job) => String(job?.id || '')).filter(Boolean));
    setLastBatch(null);
  }, [isOpen, sourceJobs]);

  const toggleAction = (action) => {
    setActions((prev) => prev.includes(action) ? prev.filter((x) => x !== action) : [...prev, action]);
  };

  const toggleSelectedJob = (jobId) => {
    const id = String(jobId || '');
    if (!id) return;
    setSelectedJobIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
    setLastBatch(null);
  };

  const selectAllSheetJobs = () => {
    setSelectedJobIds(sourceJobs.map((job) => String(job?.id || '')).filter(Boolean));
    setLastBatch(null);
  };

  const selectNoSheetJobs = () => {
    setSelectedJobIds([]);
    setLastBatch(null);
  };

  const hydratePickSheetImages = useCallback(async (sourceJobs) => {
    const list = Array.isArray(sourceJobs) ? sourceJobs : [];
    if (!currentUser || list.length === 0) return list;

    const missingSkus = Array.from(new Set(list
      .filter((job) => !String(job?.meta?.productImageUrl || job?.meta?.imageUrl || '').trim())
      .map(getPickSheetSku)
      .filter(Boolean)
      .filter((sku) => !pickSheetImageCache[sku])));

    let nextCache = pickSheetImageCache;
    if (missingSkus.length > 0) {
      const token = await currentUser.getIdToken();
      const params = new URLSearchParams({ skus: missingSkus.join(',') });
      const resp = await fetch(`${apiBase}/api/v1/print-labels/product-images?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.success !== true) throw new Error(data.message || 'Failed to load product images.');
      nextCache = { ...pickSheetImageCache, ...(data.images || {}) };
      setPickSheetImageCache(nextCache);
    }

    return list.map((job) => {
      const sku = getPickSheetSku(job);
      const found = nextCache[sku] || {};
      if (!found.productImageUrl && !found.productTitle) return job;
      return {
        ...job,
        meta: {
          ...(job.meta || {}),
          productImageUrl: job?.meta?.productImageUrl || found.productImageUrl || '',
          productTitle: job?.meta?.productTitle || found.productTitle || '',
        },
      };
    });
  }, [apiBase, currentUser, pickSheetImageCache]);

  const handlePrint = () => {
    if (jobsToPrint.length === 0) return;
    setPrinting(true);
    setError('');
    try {
      printLaserSheet({ jobs: jobsToPrint, layout, startSlot: nextSlot });
      setLastBatch({
        jobIds: jobsToPrint
          .filter((job) => String(job?.status || '').toLowerCase() === 'pending')
          .map((job) => job.id),
        jobs: jobsToPrint,
        count: jobsToPrint.length,
        startSlot: nextSlot,
        nextSlot: nextSlotAfterPrint,
      });
    } catch (e) {
      setError(e?.message || 'Failed to open print window.');
    } finally {
      setPrinting(false);
    }
  };

  const markSinglePrinted = async (job) => {
    if (isBatchMode) return;
    if (!currentUser || !isPrintablePendingJob(job)) return;
    const jobId = String(job.id || '');
    setRowSavingId(jobId);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(`${apiBase}/api/v1/print-labels/laser-sheet/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobIds: [jobId],
          nextSlot,
          markPrinted: true,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.success !== true) throw new Error(data.message || 'Failed to mark label printed.');
      setSelectedJobIds((prev) => prev.filter((id) => id !== jobId));
      setLastBatch(null);
      await loadJobs();
    } catch (e) {
      setError(e?.message || 'Failed to mark label printed.');
    } finally {
      setRowSavingId('');
    }
  };

  const confirmPrinted = async ({ closeAfter = false } = {}) => {
    if (isBatchMode) {
      setLastBatch(null);
      if (closeAfter) onClose();
      return;
    }
    if (!currentUser || !lastBatch) return;
    setSaving(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const resp = await fetch(`${apiBase}/api/v1/print-labels/laser-sheet/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobIds: lastBatch.jobIds,
          nextSlot: lastBatch.nextSlot,
          markPrinted,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.success !== true) throw new Error(data.message || 'Failed to confirm print.');
      const noJobsMarked = markPrinted && Number(data.printedCount || 0) === 0 && lastBatch.jobIds.length > 0;
      setNextSlot(normalizeSlot(data.nextSlot, layout));
      setLastBatch(null);
      await loadJobs();
      if (noJobsMarked) {
        setError('Print confirmed, but no pending jobs were marked printed. Refresh and try again.');
      }
      if (closeAfter && !noJobsMarked) {
        onClose();
      }
    } catch (e) {
      setError(e?.message || 'Failed to confirm print.');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdfAndConfirm = async () => {
    if (!lastBatch) return;
    const ok = window.confirm('Did the labels print correctly?');
    if (!ok) return;
    setGeneratingPdf(true);
    setError('');
    try {
      const jobsWithImages = await hydratePickSheetImages(lastBatch.jobs || []);
      printLabelPickSheetPdf({ jobs: jobsWithImages, startSlot: lastBatch.startSlot });
    } catch (e) {
      setError(e?.message || 'Failed to generate pick sheet PDF.');
      setGeneratingPdf(false);
      return;
    }
    setGeneratingPdf(false);
    await confirmPrinted({ closeAfter: true });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} contentClassName="label-print-modal">
      <div className="lpm">
        <div className="lpm-header">
          <div>
            <h2>{isBatchMode ? `Print Batch Labels${batchId ? `: ${batchId}` : ''}` : 'Print Labels'}</h2>
            <p>
              {isBatchMode
                ? 'Preprint all labels for this production batch on ST-33 A4 laser sticker sheets.'
                : 'Print pending or previous labels on ST-33 A4 laser sticker sheets.'}
            </p>
          </div>
          <button type="button" className="lpm-btn" onClick={() => { loadSettings(); if (!isBatchMode) loadJobs(); }}>
            Refresh
          </button>
        </div>

        {error && <div className="lpm-alert error">{error}</div>}

        {!isBatchMode && (
          <>
            <section className="lpm-section">
              <h3>Label Type</h3>
              <div className="lpm-check-row">
                {ACTION_OPTIONS.map((option) => (
                  <label className="lpm-check" key={option.value}>
                    <input type="checkbox" checked={actions.includes(option.value)} onChange={() => toggleAction(option.value)} />
                    <span>{option.label}</span>
                  </label>
                ))}
                <button type="button" className="lpm-btn" onClick={() => setActions(ACTION_OPTIONS.map((o) => o.value))}>All</button>
                <button type="button" className="lpm-btn" onClick={() => setActions([])}>None</button>
              </div>
            </section>

            <section className="lpm-section">
              <h3>Job Source</h3>
              <div className="lpm-grid">
                <label className="lpm-field">
                  <span>Source</span>
                  <select value={mode} onChange={(e) => setMode(e.target.value)}>
                    {MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                {mode === 'specific_date' && (
                  <label className="lpm-field">
                    <span>Date</span>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </label>
                )}
                {mode === 'date_range' && (
                  <>
                    <label className="lpm-field">
                      <span>From</span>
                      <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                    </label>
                    <label className="lpm-field">
                      <span>To</span>
                      <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                    </label>
                  </>
                )}
                <label className="lpm-field">
                  <span>Search</span>
                  <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="SKU, piece, order..." />
                </label>
              </div>
              <label className="lpm-check lpm-source-check">
                <input
                  type="checkbox"
                  checked={includePrinted}
                  onChange={(e) => setIncludePrinted(e.target.checked)}
                />
                <span>Include already printed labels</span>
              </label>
            </section>
          </>
        )}

        <section className="lpm-section">
          <h3>Sheet Start Slot</h3>
          <div className="lpm-summary">
            <label className="lpm-field">
              <span>Start slot</span>
              <input type="number" min="1" max={layout.labelsPerSheet || 33} value={nextSlot} onChange={(e) => setNextSlot(normalizeSlot(e.target.value, layout))} />
            </label>
            <div><strong>{sourceJobs.length}</strong><span>{isBatchMode ? ' batch labels' : ' matching labels'}</span></div>
            <div><strong>{firstPagePrintCount}</strong><span> on first sheet</span></div>
            <div><strong>{pageCount}</strong><span> sheet{pageCount === 1 ? '' : 's'}</span></div>
            <div><strong>{nextSlotAfterPrint}</strong><span> next slot</span></div>
          </div>
          <div className="lpm-sheet">
            {Array.from({ length: Number(layout.labelsPerSheet || 33) }).map((_, index) => {
              const slot = index + 1;
              const active = slot >= nextSlot && slot < nextSlot + firstPagePrintCount;
              return (
                <button
                  type="button"
                  key={slot}
                  className={`lpm-slot ${slot === nextSlot ? 'start' : ''} ${active ? 'active' : ''}`}
                  onClick={() => setNextSlot(slot)}
                >
                  {slot}
                </button>
              );
            })}
          </div>
        </section>

        <section className="lpm-section">
          <h3>Preview & Print</h3>
          {loading ? (
            <div className="lpm-empty">Loading labels...</div>
          ) : sourceJobs.length === 0 ? (
            <div className="lpm-empty">No labels match this selection.</div>
          ) : (
            <>
              <div className="lpm-selection-bar">
                <strong>{jobsToPrint.length} / {sourceJobs.length} selected</strong>
                <button type="button" className="lpm-btn small" onClick={selectAllSheetJobs}>All</button>
                <button type="button" className="lpm-btn small" onClick={selectNoSheetJobs}>None</button>
                {pageCount > 1 && <span>{pageCount} sheets: first starts at slot {nextSlot}, then full sheets from slot 1</span>}
              </div>
              <div className="lpm-job-list">
                {sourceJobs.map((job) => {
                  const jobId = String(job?.id || '');
                  const checked = selectedJobIdSet.has(jobId);
                  const canDone = !isBatchMode && isPrintablePendingJob(job);
                  return (
                    <div className={`lpm-job ${checked ? 'selected' : ''}`} key={job.id}>
                      <label className="lpm-job-select" title="Include in this print">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelectedJob(jobId)}
                        />
                      </label>
                      <span>{actionLabel(job?.meta?.action)}</span>
                      <strong>{job?.payload?.line1 || '-'}</strong>
                      <em>{job?.payload?.line2 || '-'}</em>
                      <small>{isBatchMode ? (job?.meta?.orderDisplayId ? `Order ${job.meta.orderDisplayId}` : 'Batch preprint') : formatJobTime(job)}</small>
                      {!isBatchMode && (
                        <button
                          type="button"
                          className="lpm-btn small"
                          onClick={() => markSinglePrinted(job)}
                          disabled={!canDone || saving || rowSavingId === jobId}
                          title={canDone ? 'Mark this label as printed' : 'Only pending print jobs can be marked done'}
                        >
                          {rowSavingId === jobId ? '...' : 'Done'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <div className="lpm-actions">
            <button type="button" className="lpm-btn primary" onClick={handlePrint} disabled={printing || jobsToPrint.length === 0}>
              {printing ? 'Opening...' : `Print ${jobsToPrint.length} labels`}
            </button>
            {lastBatch && (
              <>
                <button type="button" className="lpm-btn primary" onClick={handleGeneratePdfAndConfirm} disabled={saving || generatingPdf}>
                  {generatingPdf ? 'Generating...' : saving ? 'Saving...' : 'Generate PDF'}
                </button>
                <button type="button" className="lpm-btn" onClick={() => setLastBatch(null)} disabled={saving || generatingPdf}>
                  No, try again
                </button>
              </>
            )}
          </div>
          {lastBatch && (
            <div className="lpm-alert warn">
              {isBatchMode
                ? `This batch preprint opened ${lastBatch.count} labels from slot ${lastBatch.startSlot}. Generate the PDF if you need the pick sheet.`
                : `Click Generate PDF only after confirming the labels printed correctly. This print opened ${lastBatch.count} labels from slot ${lastBatch.startSlot}.`}
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}
